import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body2,
    Box,
    Flex,
    HorizontalDivider,
    IconChevronRight,
    OutlinedCard,
    Stack,
    Subtitle1,
    Subtitle2,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { useActivityConfig } from '@phx/myphx-lib';

import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    PrescriberOrderStatus,
    RenewalStatus,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { formatToLocalizedDate } from '../../../util';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';

type LastActivityCardProps = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const LastActivityCard = ({
    prescriberOrder,
}: LastActivityCardProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const configOverride = useMedicationConfigOverride();
    const { enableRxCancelLilly, enableRxRenewalLilly } = useFlags();

    const provider = getFragment(prescriberOrder.assignedPharmacyProvider);

    const providerOverrides = provider?.id
        ? configOverride.pharmacyOverrides[provider.id]
        : null;
    const providerName =
        providerOverrides?.name ??
        (provider?.__typename === 'MailOrderProvider' ||
        provider?.__typename === 'PharmacyProvider'
            ? provider.name
            : 'Unknown Provider');

    const navigateToOrderConfirmation = () => {
        navigate(
            getAbsoluteRoute('order.prescriberOrderId.confirmation', {
                prescriberOrderId: prescriberOrder.drxId ?? prescriberOrder.id,
            })
        );
    };

    const navigateToRenewalStatus = () => {
        navigate(
            getAbsoluteRoute('order.prescriberOrderId.renewalStatus', {
                prescriberOrderId: prescriberOrder.id,
            })
        );
    };

    const activityConfigs = useActivityConfig({
        prescriberOrder,
        navigateToRenewalStatus,
        navigateToOrderConfirmation,
        pharmacyName: providerName,
        enableRxCancel: enableRxCancelLilly,
        enableRxRenewal: enableRxRenewalLilly,
        RenewalStatus,
        PrescriberOrderStatus,
    });

    return (
        <OutlinedCard>
            <Stack gap="xs">
                <Subtitle1>{t('orderSummary.lastActivity')}</Subtitle1>
                <HorizontalDivider />
                <Stack gap="xs">
                    {activityConfigs.map((activityConfig, index) => (
                        <Stack key={index} gap="xxxs">
                            <Flex gap="xs" justify="space-between">
                                <Subtitle2>{activityConfig.label}</Subtitle2>
                                {activityConfig.timestamp && (
                                    <Body2>
                                        {formatToLocalizedDate(
                                            new Date(activityConfig.timestamp),
                                            {
                                                dateStyle: 'medium',
                                                timeZone:
                                                    activityConfig.timeZone,
                                            },
                                            i18n.language
                                        )}
                                    </Body2>
                                )}
                            </Flex>
                            {activityConfig.additionalContent}
                            {activityConfig.action && (
                                <Box>
                                    <TextButton
                                        RightIcon={IconChevronRight}
                                        onClick={activityConfig.action.onClick}
                                    >
                                        {activityConfig.action.label}
                                    </TextButton>
                                </Box>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </OutlinedCard>
    );
};
